/**
 * Endpoints de **Gestión → Administración → Conectividad Hermes**
 * (`/api/gestion/administracion/hermes/*`).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { env } from '@/config/env';
import { apiClient } from '@/lib/http/api-client';
import { getSessionProvider } from '@/lib/http/session-provider';

import type {
  CierreHermes,
  CierresHermesRespuesta,
  ConectividadHermes,
  ConfiguracionHermes,
  TransaccionesHermesRespuesta,
} from './hermes.types';

const CLAVE = 'gestion';

export const hermesKeys = {
  configuracion: () => [CLAVE, 'administracion', 'hermes', 'configuracion'] as const,
  transacciones: (fecha: string) => [CLAVE, 'administracion', 'hermes', 'transacciones', fecha] as const,
  cierres: () => [CLAVE, 'administracion', 'hermes', 'cierres'] as const,
};

export const hermesApi = {
  configuracion: (signal?: AbortSignal): Promise<ConfiguracionHermes> =>
    apiClient.get<ConfiguracionHermes>('/api/gestion/administracion/hermes/configuracion', { signal }),

  conectividad: (): Promise<ConectividadHermes> =>
    apiClient.get<ConectividadHermes>('/api/gestion/administracion/hermes/conectividad'),

  transacciones: (fecha: string, signal?: AbortSignal): Promise<TransaccionesHermesRespuesta> =>
    apiClient.get<TransaccionesHermesRespuesta>(
      `/api/gestion/administracion/hermes/transacciones?fecha=${encodeURIComponent(fecha)}`,
      { signal },
    ),

  cierres: (signal?: AbortSignal): Promise<CierresHermesRespuesta> =>
    apiClient.get<CierresHermesRespuesta>('/api/gestion/administracion/hermes/cierres', { signal }),

  crearCierre: (fecha: string, observaciones: string): Promise<{ id: number; fecha: string; total_transacciones: number; monto_total: number }> =>
    apiClient.post('/api/gestion/administracion/hermes/cierres', {
      body: { fecha, observaciones },
    }),

  actualizarCierre: (fecha: string, observaciones: string): Promise<{ fecha: string; total_transacciones: number; monto_total: number }> =>
    apiClient.put('/api/gestion/administracion/hermes/cierres', {
      body: { fecha, observaciones },
    }),

  deshacerCierre: (fecha: string): Promise<{ fecha: string; mensaje: string }> =>
    apiClient.post('/api/gestion/administracion/hermes/cierres/deshacer', {
      body: { fecha },
    }),

  /** Descarga de Excel: requiere el token, así que se pide con `fetch`. */
  exportarTransacciones: async (fecha: string): Promise<void> => {
    await descargar(`/api/gestion/administracion/hermes/transacciones/exportar?fecha=${encodeURIComponent(fecha)}`, `Hermes_Transacciones_${fecha}.xlsx`);
  },

  exportarCierres: async (): Promise<void> => {
    const fecha = new Date().toISOString().slice(0, 10);
    await descargar('/api/gestion/administracion/hermes/cierres/exportar', `Hermes_Historial_Cierres_${fecha}.xlsx`);
  },
};

/** Descarga un Excel autenticado y lo guarda en el navegador. */
async function descargar(ruta: string, nombre: string): Promise<void> {
  const token = getSessionProvider()?.getAccessToken();
  const respuesta = await fetch(`${env.apiUrl}${ruta}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });

  if (!respuesta.ok) {
    throw new Error(`No se pudo exportar (HTTP ${respuesta.status}).`);
  }

  const blob = await respuesta.blob();
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement('a');

  enlace.href = url;
  enlace.download = nombre;
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
  URL.revokeObjectURL(url);
}

/** Cierres con caché y refresco tras crear/actualizar/deshacer. */
export function useCierres() {
  return useQuery({
    queryKey: hermesKeys.cierres(),
    queryFn: ({ signal }) => hermesApi.cierres(signal),
  });
}

/** Mutación que refresca transacciones y cierres al completar. */
export function useAccionCierre() {
  const queryClient = useQueryClient();

  const invalidar = (fecha: string) => {
    queryClient.invalidateQueries({ queryKey: hermesKeys.transacciones(fecha) });
    queryClient.invalidateQueries({ queryKey: hermesKeys.cierres() });
  };

  const crear = useMutation({
    mutationFn: ({ fecha, observaciones }: { fecha: string; observaciones: string }) =>
      hermesApi.crearCierre(fecha, observaciones),
    onSuccess: (_datos, variables) => invalidar(variables.fecha),
  });

  const actualizar = useMutation({
    mutationFn: ({ fecha, observaciones }: { fecha: string; observaciones: string }) =>
      hermesApi.actualizarCierre(fecha, observaciones),
    onSuccess: (_datos, variables) => invalidar(variables.fecha),
  });

  const deshacer = useMutation({
    mutationFn: (fecha: string) => hermesApi.deshacerCierre(fecha),
    onSuccess: (_datos, fecha) => invalidar(fecha),
  });

  return { crear, actualizar, deshacer };
}

export type { CierreHermes };
